import {
  Injectable,
  computed,
  inject,
  signal
} from '@angular/core';
import { Observable, tap } from 'rxjs';

import { round, syncWithLocalStorage } from '@core/helpers';

import { AuthState } from '../users';
import { CacheBurstService } from './cache-burst.service';

import {
  ApiResponse,
  Item,
  ShopFilters,
  SteamApp
} from './items.entity';
import {
  extraFiltersPass,
  marketNameFilterPass,
  onlyAvailableFilterPass,
  priceFilterPass
} from './filters';
import { InventoryService } from './inventory.service';

@Injectable()
export class ShopState {
  private readonly authState = inject(AuthState);
  private readonly inventoryService = inject(InventoryService);
  private readonly cacheBurstService = inject(CacheBurstService);

  public readonly appId = signal<SteamApp>(SteamApp.CSGO);
  public readonly selectedShopIds = signal<number[]>([]);
  public readonly selectedShopAmountUsd = signal<number>(0);
  public readonly shopHidden = signal<boolean>(true);
  public readonly filters = signal<ShopFilters>({
    marketName: null,
    priceUsd: this.authState.user()?.balanceUsd || null,
    extraFilters: null,
    onlyAvailable: true
  });

  private readonly itemsDb = signal<Item[]>([]);

  public readonly filteredItems = computed(() => {
    const filters = this.filters();
    const itemsDb = this.itemsDb();

    return itemsDb
      .filter((item: Item) => {
        return marketNameFilterPass(item.marketName, filters.marketName) &&
               priceFilterPass(item.priceUsd, filters.priceUsd) &&
               onlyAvailableFilterPass(item.stock, filters.onlyAvailable) &&
               extraFiltersPass(item, filters.extraFilters);
      })
      .sort((a: Item, b: Item) => b.priceUsd - a.priceUsd);
  });

  public readonly selectedFilteredItems = computed(() => {
    const filteredItems = this.filteredItems();
    const selectedShopIds = this.selectedShopIds();

    return filteredItems.filter((item: Item) => selectedShopIds.includes(item.id));
  });

  private readonly localStorageEffect = syncWithLocalStorage('shop', {
    appId: this.appId
  });

  setAppId(appId?: SteamApp) {
    appId && this.appId.set(appId);

    return this.loadDatabase();
  }

  loadDatabase(): Observable<ApiResponse.ItemsList> {
    const appId = this.appId();

    this.resetDatabase();

    return this.cacheBurstService.getItems(appId, true).pipe(
      tap((response) => {
        const items = response.items.map((item) => {
          return {
            ...item,
            priceUsd: Number(item.priceUsd)
          };
        });

        this.itemsDb.set(items);
      })
    );
  }

  resetDatabase(): void {
    this.itemsDb.set([]);
  }

  selectShopItem(item: Item): void {
    this.selectedShopIds.update((ids) => [...ids, item.id]);
    this.selectedShopAmountUsd.update((value) => round(value + item.priceUsd));
  }

  unselectShopItem(item: Item): void {
    this.selectedShopIds.update((ids) => ids.filter((id) => item.id !== id));
    this.selectedShopAmountUsd.update((value) => round(value - item.priceUsd));
  }

  toggleHidden(): void {
    this.shopHidden.update((value) => !value);
    this.resetSelectedItems();
  }

  applyShopFilter(key: keyof ShopFilters, value: any): void {
    this.filters.update((filters) => {
      return {
        ...filters,
        [key]: value
      };
    });
  }

  resetFilters(): void {
    this.filters.set({
      marketName: null,
      priceUsd: this.authState.user()?.balanceUsd || null,
      extraFilters: null,
      onlyAvailable: true
    });
  }

  resetSelectedItems(): void {
    this.selectedShopAmountUsd.set(0);
    this.selectedShopIds.set([]);
  }

  reset(): void {
    this.shopHidden.set(true);

    this.resetSelectedItems();
    this.resetFilters();
  }
}
